import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import AppLayout from "layouts/appLayout/AppLayout";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { OpportunityController } from "services/opportunity/controller";
import { Button, Dropdown, Input, Link } from "stories";
import { Dialog } from "stories/components/dialog";
import { useFetchState } from "utils/customHooks/useFetchState";
import { useToast } from "utils/customHooks/useToast";
import Filter from "./elements/filter/Filter";
import OpportunityCard from "./elements/opportunityCard/OpportunityCard";
import classes from "./Opportunities.module.scss";

const opportunityController = OpportunityController();
const sortList = [
  {
    id: "1",
    title: "Amount Requested (Ascending)",
    value: "amount_requested-0",
  },
  {
    id: "2",
    title: "Amount Requested (Descending)",
    value: "amount_requested-1",
  },
  { id: "3", title: "Creation Date (Ascending)", value: "create_time-0" },
  { id: "4", title: "Creation Date (Descending)", value: "create_time-1" },
];
const loadPerPage = 10;
const Opportunities = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [fetchState, sendRequest] = useFetchState();
  const [deletingOpportunity, setDeletingOpportunity] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filters, setFilters] = useState({
    order: "create_time",
    desc: "1",
    search: "",
    offset: 0,
    limit: loadPerPage,
  });

  const { showToast } = useToast();

  useEffect(() => {
    sendRequest(opportunityController.read, filters);
  }, [filters]); //eslint-disable-line

  useEffect(() => {
    if (!fetchState.isLoading && !fetchState.error && fetchState.data) {
      if (fetchState.data.length > 0) {
        setOpportunities([...opportunities, ...fetchState.data]);
      }
    }
  }, [fetchState]); //eslint-disable-line
  const handleSortChanged = (e) => {
    setOpportunities([]);
    const sort = e.target.value.split("-");
    setFilters({
      ...filters,
      offset: 0,
      order: sort[0],
      desc: sort[1],
    });
  };
  const openDeleteDialog = (opportunityId) => {
    setDeletingOpportunity(opportunityId);
  };

  const handleCloseDeleteDialog = () => {
    setDeletingOpportunity(null);
  };

  const handleSerachValue = useCallback(
    (e) => {
      setOpportunities([]);
      setFilters({
        ...filters,
        offset: 0,
        search: e.target.value,
      });
    },
    [filters]
  );
  const debouncedHandleSearchValue = useMemo(
    () => debounce(handleSerachValue, 300),
    [handleSerachValue]
  );

  useEffect(() => {
    return () => {
      debouncedHandleSearchValue.cancel();
    };
  }, []); // eslint-disable-line

  const onDelete = () => {
    setIsDeleting(true);
    opportunityController
      .deleteOpportunity(deletingOpportunity)
      .then(() => {
        showToast("The opportunity deleted successfully", "success");
        setOpportunities([]);
        sendRequest(opportunityController.read, {
          ...filters,
          offset: 0,
          limit: filters.offset + filters.limit,
        });
      })
      .finally(() => {
        setDeletingOpportunity(null);
        setIsDeleting(false);
      });
  };

  const handleLoadMore = () => {
    setFilters({
      ...filters,
      offset: filters.offset + loadPerPage,
      limit: loadPerPage,
    });
  };

  return (
    <AppLayout title={"Opportunities"} hideTopNav>
      <Helmet>
        <title>Opportunities | Fundever</title>
      </Helmet>
      <div className={"min-vh-100"}>
        <Filter />
        <div className={classes["main-wrapper"]}>
          <div className={classes["heading"]}>
            <div className={"d-flex justify-content-between w-100"}>
              <div className="d-flex gap-3">
                <Input
                  placeholder={"Search Project Name ..."}
                  shadow
                  white
                  ignoreOffsetTop
                  startAdornment={<MagnifyingGlassIcon className="icon-md" />}
                  onChange={debouncedHandleSearchValue}
                  style={{ width: "250px" }}
                />
                <Dropdown
                  startAdornment="Sort by:"
                  value={filters.order + "-" + filters.desc}
                  list={sortList}
                  ignoreOffsetTop
                  onChange={handleSortChanged}
                />
              </div>
              <Link to={"create"}>
                <div className={classes["button-wrapper"]}>
                  <Button
                    label={
                      <div className="d-flex align-items-center gap-2">
                        <PlusCircleIcon className="icon-lg" />
                        <span>Create New Opportunity</span>
                      </div>
                    }
                  />
                </div>
              </Link>
            </div>
          </div>
          {fetchState.isLoading && opportunities.length === 0 && (
            <div className={classes["content-wrapper"]}>
              <OpportunityCard loading />
            </div>
          )}
          {!fetchState.isLoading && opportunities.length === 0 && (
            <div className={classes["empty-state-wrapper"]}>
              {filters.search ? (
                <>
                  <div className="font-16 font-gray fw-bold">
                    No Opportunity found!
                  </div>
                  <div className="font-14 font-gray">
                    It seems that no opportunity matched with what you are
                    looking for
                  </div>
                </>
              ) : (
                <>
                  <div className="font-16 font-gray fw-bold">Good Morning!</div>
                  <div className="font-14 font-gray">
                    It looks like you have no opportunities created yet.
                  </div>
                </>
              )}
            </div>
          )}
          {opportunities.length > 0 && (
            <div className={classes["content-wrapper"]}>
              {opportunities.map((item) => (
                <OpportunityCard
                  data={item}
                  key={item.id}
                  onDelete={() => openDeleteDialog(item.id)}
                  isDeleting={deletingOpportunity === item.id}
                />
              ))}
              <Dialog
                isOpen={!!deletingOpportunity}
                title="Delete Opportunity Confirmation"
                description="Are you sure you want to delete this opportunity?"
                okText="Yes, Delete"
                cancelText="Don't delete"
                onClose={handleCloseDeleteDialog}
                onConfirm={onDelete}
                isLoading={isDeleting}
                variant="delete"
              />
            </div>
          )}
          {fetchState.data && fetchState.data.length >= loadPerPage && (
            <div className="d-flex justify-content-center">
              <div className="w-sm">
                <Button
                  label="Load More"
                  variant="secondary"
                  loading={fetchState.isLoading}
                  onClick={handleLoadMore}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default Opportunities;
